import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssistantActivity } from '@/lib/ai-assistant/deriveAssistantActivity';

const ELAPSED_FROM_SECONDS = 5;
const LONG_WAIT_SECONDS = 45;

// Seconds since `active` last became true; 0 while inactive. Lives here, not
// in the panel, so the tick re-renders only this line.
function useElapsedSeconds(active: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    setSeconds(0);
    if (!active) return undefined;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return seconds;
}

function Dots() {
  return (
    <span className="flex items-center gap-0.5" aria-hidden="true" data-testid="assistant-activity-dots">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );
}

// One line under the conversation saying what the assistant is doing. The
// seconds count the whole turn (all its phases) and are hidden from screen
// readers, which would otherwise announce every tick.
export function AssistantActivityLine({ activity }: { activity: AssistantActivity }) {
  const { t } = useTranslation();
  const counting = activity.kind !== 'idle' && activity.kind !== 'awaiting_confirmation';
  const elapsedSeconds = useElapsedSeconds(counting);
  if (activity.kind === 'idle') return null;
  let label: string;
  switch (activity.kind) {
    case 'sending': label = t('aiAssistant.activity.sending'); break;
    case 'editing': label = t('aiAssistant.activity.editing'); break;
    case 'capturing': label = t('aiAssistant.activity.capturing'); break;
    case 'awaiting_confirmation': label = t('aiAssistant.activity.awaitingConfirmation'); break;
    case 'tool': {
      const tool = t(`aiAssistant.tools.${activity.toolName}`, { defaultValue: activity.toolTitle || activity.toolName });
      label = t(`aiAssistant.activity.tools.${activity.toolName}`, {
        defaultValue: t('aiAssistant.activity.tool', { tool }),
      });
      break;
    }
    default: label = t('aiAssistant.activity.thinking');
  }
  const waiting = activity.kind === 'awaiting_confirmation';
  return (
    <div
      className="flex items-center gap-2 px-1 text-xs text-muted-foreground"
      data-testid="assistant-thinking"
      role="status"
      aria-live="polite"
    >
      {!waiting && <Dots />}
      <span>{label}</span>
      {!waiting && elapsedSeconds >= ELAPSED_FROM_SECONDS && (
        <span className="tabular-nums" aria-hidden="true">
          · {t('aiAssistant.activity.elapsed', { seconds: elapsedSeconds })}
        </span>
      )}
      {!waiting && elapsedSeconds >= LONG_WAIT_SECONDS && (
        <span aria-hidden="true">· {t('aiAssistant.activity.longWait')}</span>
      )}
    </div>
  );
}
